import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import AddPaymentMethodModal from './AddPaymentMethodModal';
import { 
  User, 
  CreditCard, 
  Crown, 
  FileText,
  Settings,
  Shield,
  Calendar,
  Activity,
  History,
  Lock,
  Bell,
  Download,
  RefreshCw,
  Edit3,
  Check,
  X,
  AlertCircle,
  AlertTriangle,
  Star,
  ArrowUp,
  ArrowDown,
  Zap,
  Plus,
  MessageCircle,
  Send,
  XCircle,
  LifeBuoy
} from 'lucide-react';
import SupportTickets from './SupportTickets';

const RefreshAccountButton = () => {
  const { refreshUser } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshUser();
    setRefreshing(false);
  };

  return (
    <Button
      onClick={handleRefresh}
      disabled={refreshing}
      variant="outline"
      size="sm"
      className="flex items-center"
    >
      <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
      {refreshing ? 'Refreshing...' : 'Refresh'}
    </Button>
  );
};

const AccountPage = () => {
  const { user, refreshUser } = useAuth();
  const [accountData, setAccountData] = useState({
    accountCreated: null,
    lastLogin: null
  });
  const [loading, setLoading] = useState({
    subscription: false
  });
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: ''
  });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [userUsage, setUserUsage] = useState({
    legalMatters: 0,
    publicNotices: 0,
    storage: '0GB',
    legalMattersLimit: 'Unlimited',
    publicNoticesLimit: 'Unlimited',
    storageLimit: 'Unlimited'
  });
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showBillingHistoryModal, setShowBillingHistoryModal] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [cancellationData, setCancellationData] = useState({
    reason: '',
    feedback: ''
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [accountTab, setAccountTab] = useState('settings');
  const { toast } = useToast();

  // Get backend URL from environment
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Subscription plans data
  const subscriptionPlans = [
    {
      id: 'free',
      name: 'Pro Se Starter',
      price: 0,
      period: 'Forever',
      description: 'Basic access to legal tools and resources',
      features: [
        'Basic AI Assistant',
        'Limited Document Templates',
        'Community Support',
        'Basic Legal Resources'
      ],
      color: 'from-gray-400 to-gray-500',
      icon: FileText,
      popular: false
    },
    {
      id: 'basic',
      name: 'Justice Seeker',
      price: 9.99,
      period: 'month',
      description: 'Essential legal tools for personal use',
      features: [
        '5 Legal Matters per month',
        'AI Remedy Generator + Template Suite',
        '50+ Custom Templates per month',
        'Access to Mailing Room',
        'Printable Affidavits and Templates',
        'Live Chat Support (48 hrs)'
      ],
      color: 'from-blue-500 to-blue-600',
      icon: Shield,
      popular: true
    },
    {
      id: 'premium',
      name: 'Sovereign Shield',
      price: 99,
      period: 'month',
      description: 'Advanced features for power users and professionals',
      features: [
        'Unlimited Legal Matters',
        'Unlimited Document Generation',
        'Advanced AI Remedy Builder',
        'Unlimited Legal Simulations',
        'Secure Audit Trail',
        'AI Document Review + Suggestions',
        'Access to Sovereign Toolbox',
        'Premium Content Access'
      ],
      color: 'from-red-500 to-red-600',
      icon: Crown,
      popular: false
    },
    {
      id: 'lifetime',
      name: 'Defender of the People',
      price: 1497,
      period: 'one-time',
      description: 'Lifetime access to all features',
      features: [
        'Everything in Sovereign Shield',
        'Lifetime Access (No Monthly Fees)',
        'Priority Support',
        'Exclusive Content Updates',
        'Advanced Training Materials',
        'Direct Access to Legal Experts'
      ],
      color: 'from-purple-600 to-indigo-700',
      icon: Star,
      popular: false
    }
  ];

  // Handle subscription upgrade
  const handleSubscriptionUpgrade = async (planId) => {
    setLoading(prev => ({ ...prev, subscription: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planId: planId,
          currentPlan: user?.subscription_plan
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.checkoutUrl) {
          // Redirect to Stripe checkout
          window.location.href = result.checkoutUrl;
        } else {
          toast({
            title: "Subscription Updated",
            description: `Successfully upgraded to ${selectedPlan?.name}`,
            variant: "default"
          });
          setShowSubscriptionModal(false);
          // Refresh user data
          window.location.reload();
        }
      } else {
        throw new Error('Subscription upgrade failed');
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast({
        title: "Upgrade Failed",
        description: "Unable to process subscription upgrade. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, subscription: false }));
    }
  };

  // Get plan comparison
  const getCurrentPlanIndex = () => {
    return subscriptionPlans.findIndex(plan => plan.id === user?.subscription_plan) || 0;
  };

  const getAvailableUpgrades = () => {
    const currentIndex = getCurrentPlanIndex();
    return subscriptionPlans.filter((plan, index) => index > currentIndex);
  };

  const getAvailableDowngrades = () => {
    const currentIndex = getCurrentPlanIndex();
    return subscriptionPlans.filter((plan, index) => index < currentIndex && plan.id !== 'free');
  };

  const handleChangePassword = async () => {
    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match.",
        variant: "destructive"
      });
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 8) {
      toast({
        title: "Error", 
        description: "New password must be at least 8 characters long.",
        variant: "destructive"
      });
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (response.ok) {
        toast({
          title: "Password Changed",
          description: "Your password has been changed successfully."
        });
        
        // Reset form and close modal
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowChangePasswordModal(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to change password. Please try again.",
        variant: "destructive"
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        toast({
          title: "Profile Updated",
          description: "Your profile information has been updated successfully."
        });
        setEditingProfile(false);
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan?.toLowerCase()) {
      case 'premium':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'lifetime':
        return 'bg-gold-100 text-gold-800 border-gold-200';
      case 'pro':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPlanIcon = (plan) => {
    if (plan === 'premium' || plan === 'lifetime') {
      return <Crown className="w-4 h-4" />;
    }
    return <User className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toFixed(2)}`;
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || ''
      });
      fetchUserUsage();
      fetchCustomerInfo();
      fetchSubscriptionStatus();
    }
  }, [user]);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/payment/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/payment/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: cancellationData.reason,
          feedback: cancellationData.feedback
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Subscription Cancelled",
          description: result.message,
          variant: "default"
        });
        setShowCancelModal(false);
        setCancellationData({ reason: '', feedback: '' });
        // Refresh user data
        await refreshUser();
        await fetchSubscriptionStatus();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: "Cancellation Failed",
        description: error.message || "Failed to cancel subscription. Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setCancellingSubscription(false);
    }
  };

  const fetchUserUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/user/usage`, {
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }
      });

      if (response.ok) {
        const data = await response.json();
        const currentUsage = data.current_usage || {};
        const planLimits = data.plan_limits || {};
        
        const docCount = currentUsage.documents_generated || 0;
        const storageGB = (docCount * 0.1).toFixed(1);
        
        setUserUsage({
          legalMatters: currentUsage.legal_matters_count || 0,
          publicNotices: currentUsage.templates_downloaded || 0,
          storage: `${storageGB}GB`,
          legalMattersLimit: planLimits.legal_matters || 'Unlimited',
          publicNoticesLimit: planLimits.templates || 'Unlimited',
          storageLimit: planLimits.storage || 'Unlimited'
        });
      }
    } catch (error) {
      console.error('Error fetching usage data:', error);
    }
  };

  const fetchCustomerInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/billing/customer-info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomerInfo(data);
      }
    } catch (error) {
      console.error('Error fetching customer info:', error);
    }
  };

  const handleUpdateBilling = () => {
    setShowAddPaymentModal(true);
  };

  const handlePaymentMethodAdded = (paymentMethod) => {
    fetchCustomerInfo();
    toast({
      title: "Payment Method Added",
      description: `${paymentMethod.card?.brand?.toUpperCase()} ending in ${paymentMethod.card?.last4} added successfully.`,
      variant: "default"
    });
  };

  const handleViewBillingHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/billing/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const transactions = await response.json();
        setBillingHistory(transactions);
        setShowBillingHistoryModal(true);
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch billing history.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error fetching billing history:', error);
      toast({
        title: "Error",
        description: "Failed to fetch billing history.",
        variant: "destructive"
      });
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-6 pt-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p className="text-gray-600 text-sm mt-0.5">Manage your account, subscription, and support</p>
          </div>
          <div className="flex items-center space-x-3">
            <RefreshAccountButton />
            <Badge className={`${getPlanBadgeColor(user.subscription_plan)} px-3 py-1`}>
              {getPlanIcon(user.subscription_plan)}
              <span className="ml-1 font-medium">
                {user.subscription_plan?.charAt(0).toUpperCase() + user.subscription_plan?.slice(1) || 'Free'}
              </span>
            </Badge>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setAccountTab('settings')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              accountTab === 'settings' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="account-tab-settings"
          >
            <Settings className="w-4 h-4" />
            Account
          </button>
          <button
            onClick={() => setAccountTab('support')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              accountTab === 'support' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="account-tab-support"
          >
            <LifeBuoy className="w-4 h-4" />
            Support Tickets
          </button>
        </div>
      </div>

      {/* Tab content */}
      {accountTab === 'support' && (
        <div className="flex-1 overflow-y-auto">
          <SupportTickets />
        </div>
      )}

      {accountTab === 'settings' && (
      <>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Profile Information */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Profile Information</span>
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingProfile(!editingProfile)}
              >
                {editingProfile ? <X className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Full Name</label>
                  {editingProfile ? (
                    <Input
                      value={profileData.full_name}
                      onChange={(e) => setProfileData(prev => ({...prev, full_name: e.target.value}))}
                      className="mt-1"
                    />
                  ) : (
                    <p className="mt-1 text-gray-900">{user.full_name || 'Not provided'}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Email Address</label>
                  <p className="mt-1 text-gray-900">{user.email}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Account Status</label>
                  <div className="mt-1">
                    <Badge variant={user.is_active ? "success" : "destructive"}>
                      {user.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Member Since</label>
                  <p className="mt-1 text-gray-900">{formatDate(user.created_at)}</p>
                </div>
              </div>
              
              {editingProfile && (
                <div className="flex space-x-2 pt-4">
                  <Button onClick={handleProfileUpdate} size="sm">
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setEditingProfile(false)} 
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Enhanced Subscription Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Crown className="h-5 w-5" />
                <span>Subscription Management</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Plan Display */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${subscriptionPlans.find(p => p.id === user?.subscription_plan)?.color || 'from-gray-400 to-gray-500'} flex items-center justify-center`}>
                    {React.createElement(subscriptionPlans.find(p => p.id === user?.subscription_plan)?.icon || FileText, {
                      className: "w-6 h-6 text-white"
                    })}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {subscriptionPlans.find(p => p.id === user?.subscription_plan)?.name || 'Pro Se Starter'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {user?.subscription_plan === 'defender_of_the_people' ? 'Lifetime Access' : 
                       user?.subscription_plan === 'free' ? 'Free Plan' :
                       `$${subscriptionPlans.find(p => p.id === user?.subscription_plan)?.price || 0}/month`}
                    </p>
                  </div>
                </div>
                <Badge className={`${getPlanBadgeColor(user?.subscription_plan)} px-3 py-1`}>
                  {getPlanIcon(user?.subscription_plan)}
                  <span className="ml-1 font-medium">
                    {user?.subscription_plan?.charAt(0).toUpperCase() + user?.subscription_plan?.slice(1) || 'Free'}
                  </span>
                </Badge>
              </div>

              {/* Plan Features */}
              <div className="space-y-3">
                <p className="font-medium text-gray-900">Your Plan Includes:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(subscriptionPlans.find(p => p.id === user?.subscription_plan)?.features || []).map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Upgrade/Change Options */}
              <div className="space-y-4">
                <p className="font-medium text-gray-900">Subscription Options:</p>
                
                {/* Available Upgrades */}
                {getAvailableUpgrades().length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-green-700 flex items-center">
                      <ArrowUp className="w-4 h-4 mr-1" />
                      Available Upgrades
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getAvailableUpgrades().map((plan) => (
                        <div key={plan.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {React.createElement(plan.icon, { className: "w-5 h-5 text-gray-600" })}
                              <span className="font-medium text-gray-900">{plan.name}</span>
                            </div>
                            {plan.popular && (
                              <Badge className="bg-yellow-100 text-yellow-800 text-xs">Popular</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
                          <div className="space-y-3">
                            <div className="flex flex-col">
                              <span className="font-bold text-lg">
                                ${plan.price}{plan.period !== 'one-time' && plan.price > 0 && `/${plan.period}`}
                              </span>
                              {plan.period !== 'one-time' && plan.price > 0 && (
                                <span className="text-xs text-gray-500">per {plan.period}</span>
                              )}
                            </div>
                            <Button
                              size="xs"
                              onClick={() => {
                                setSelectedPlan(plan);
                                setShowSubscriptionModal(true);
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white flex items-center px-3 py-1 text-xs w-fit"
                            >
                              <ArrowUp className="w-3 h-3 mr-1" />
                              Upgrade
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Downgrades */}
                {getAvailableDowngrades().length > 0 && user?.subscription_plan !== 'defender_of_the_people' && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-blue-700 flex items-center">
                      <ArrowDown className="w-4 h-4 mr-1" />
                      Change Plan
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getAvailableDowngrades().map((plan) => (
                        <div key={plan.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center space-x-2 mb-2">
                            {React.createElement(plan.icon, { className: "w-5 h-5 text-gray-600" })}
                            <span className="font-medium text-gray-900">{plan.name}</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
                          <div className="space-y-3">
                            <div className="flex flex-col">
                              <span className="font-bold text-lg">
                                ${plan.price}{plan.period !== 'one-time' && plan.price > 0 && `/${plan.period}`}
                              </span>
                              {plan.period !== 'one-time' && plan.price > 0 && (
                                <span className="text-xs text-gray-500">per {plan.period}</span>
                              )}
                            </div>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => {
                                setSelectedPlan(plan);
                                setShowSubscriptionModal(true);
                              }}
                              className="flex items-center px-3 py-1 text-xs w-fit"
                            >
                              <ArrowDown className="w-3 h-3 mr-1" />
                              Change
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lifetime Plan Message */}
                {user?.subscription_plan === 'defender_of_the_people' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-purple-800">
                      <Star className="w-5 h-5" />
                      <span className="font-medium">Lifetime Member</span>
                    </div>
                    <p className="text-sm text-purple-700 mt-1">
                      You have lifetime access to all features. Thank you for being a Defender of the People!
                    </p>
                  </div>
                )}

                {/* Free Plan Upgrade */}
                {user?.subscription_plan === 'free' && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-blue-800 mb-2">
                      <Zap className="w-5 h-5" />
                      <span className="font-medium">Unlock Premium Features</span>
                    </div>
                    <p className="text-sm text-blue-700 mb-3">
                      Upgrade to access the Sovereign Toolbox, unlimited document generation, and advanced legal simulations.
                    </p>
                    <Button
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                      onClick={() => {
                        // Navigate to Subscription Management Hub
                        window.location.href = '/dashboard/subscription';
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      View All Plans
                    </Button>
                  </div>
                )}

                {/* Cancel Subscription Section */}
                {user?.subscription_plan && user?.subscription_plan !== 'free' && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Cancel Subscription</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {subscriptionStatus?.cancel_at_period_end 
                            ? `Your subscription will end on ${new Date(subscriptionStatus.current_period_end).toLocaleDateString()}`
                            : "You can cancel your subscription at any time"}
                        </p>
                      </div>
                      {subscriptionStatus?.cancel_at_period_end ? (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Cancelling
                        </Badge>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCancelModal(true)}
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                          data-testid="cancel-subscription-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel Subscription
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plan Comparison Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Crown className="h-5 w-5" />
              <span>Plan Comparison Chart</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Feature</th>
                    <th className="text-center p-2">Free</th>
                    <th className="text-center p-2">Justice Seeker</th>
                    <th className="text-center p-2">Sovereign Shield</th>
                    <th className="text-center p-2">Defender of the People</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-2 font-medium">Legal Matters / mo</td>
                    <td className="text-center p-2">1</td>
                    <td className="text-center p-2">5</td>
                    <td className="text-center p-2">Unlimited</td>
                    <td className="text-center p-2">Unlimited</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">Document Vault Storage</td>
                    <td className="text-center p-2">500MB</td>
                    <td className="text-center p-2">2GB</td>
                    <td className="text-center p-2">10GB</td>
                    <td className="text-center p-2">Unlimited</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">Video Library Access</td>
                    <td className="text-center p-2">🔒 Partial</td>
                    <td className="text-center p-2">Full Access</td>
                    <td className="text-center p-2">Full Access</td>
                    <td className="text-center p-2">Full Access</td>
                  </tr>
                  <tr className="border-b bg-blue-50">
                    <td className="p-2 font-bold">Price</td>
                    <td className="text-center p-2 font-bold">$0</td>
                    <td className="text-center p-2 font-bold">$9.99/mo</td>
                    <td className="text-center p-2 font-bold">$99/mo</td>
                    <td className="text-center p-2 font-bold">$1497 One-time</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* This Month's Usage and Billing Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* This Month's Usage */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center space-x-2">
                  <Activity className="h-5 w-5" />
                  <span>This Month's Usage</span>
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchUserUsage}
                  title="Refresh usage data"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm">🧾 Legal Matters</span>
                  <span className="font-medium">
                    {userUsage.legalMatters} of {userUsage.legalMattersLimit}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm">📄 Documents Generated</span>
                  <span className="font-medium">
                    {userUsage.publicNotices} of {userUsage.publicNoticesLimit}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm">💾 Storage Used</span>
                  <span className="font-medium">
                    {userUsage.storage} of {userUsage.storageLimit}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Billing Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5" />
                <span>Billing Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold mb-2">Payment Method</h3>
                    {customerInfo?.payment_methods && customerInfo.payment_methods.length > 0 ? (
                      customerInfo.payment_methods.map((pm) => (
                        <div key={pm.id} className="flex items-center space-x-2 mb-2">
                          <CreditCard className="w-5 h-5 text-gray-600" />
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-600">
                              {pm.brand?.toUpperCase()} **** **** **** {pm.last4}
                              {pm.is_default && <span className="text-xs text-blue-600 ml-1">(Default)</span>}
                            </span>
                            <p className="text-xs text-gray-500">Expires {pm.exp_month}/{pm.exp_year}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center space-x-2">
                        <CreditCard className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-500">No payment method on file</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold mb-2">Next Billing Date</h3>
                    {customerInfo?.subscriptions && customerInfo.subscriptions.length > 0 ? (
                      customerInfo.subscriptions.map((sub) => (
                        <div key={sub.id}>
                          <p className="text-sm">
                            {new Date(sub.current_period_end * 1000).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Status: {sub.status}
                            {sub.cancel_at_period_end && " (Cancels at period end)"}
                          </p>
                        </div>
                      ))
                    ) : (
                      <>
                        <p className="text-sm">
                          {user?.subscription_plan === 'lifetime' || user?.subscription_plan === 'defender_of_the_people' 
                            ? 'N/A - Lifetime Plan' 
                            : 'No active subscriptions'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {user?.subscription_plan === 'lifetime' || user?.subscription_plan === 'defender_of_the_people'
                            ? 'No recurring charges' 
                            : 'Upgrade to start billing cycle'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                
                {customerInfo?.customer_id && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Customer ID:</span>
                      <span className="text-sm text-blue-700 font-mono">{customerInfo.customer_id}</span>
                    </div>
                  </div>
                )}
                
                <div className="flex space-x-4">
                  <Button variant="outline" size="sm" onClick={handleUpdateBilling}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    Update Billing
                  </Button>
                  
                  <Button variant="outline" size="sm" onClick={handleViewBillingHistory}>
                    <FileText className="w-4 h-4 mr-2" />
                    View History
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Security & Privacy</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Password</p>
                  <p className="text-sm text-gray-600">Keep your account secure with a strong password</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowChangePasswordModal(true)}
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Subscription Change Confirmation Modal */}
      {showSubscriptionModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {getCurrentPlanIndex() < subscriptionPlans.findIndex(p => p.id === selectedPlan.id) ? 'Upgrade' : 'Change'} Subscription
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSubscriptionModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Current Plan */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Current Plan</p>
                  <div className="flex items-center space-x-2">
                    {React.createElement(subscriptionPlans.find(p => p.id === user?.subscription_plan)?.icon || FileText, {
                      className: "w-5 h-5 text-gray-600"
                    })}
                    <span className="font-medium">
                      {subscriptionPlans.find(p => p.id === user?.subscription_plan)?.name || 'Pro Se Starter'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {user?.subscription_plan === 'defender_of_the_people' ? 'Lifetime' : 
                       user?.subscription_plan === 'free' ? 'Free' :
                       `$${subscriptionPlans.find(p => p.id === user?.subscription_plan)?.price || 0}/month`}
                    </span>
                  </div>
                </div>

                <div className="flex justify-center">
                  <ArrowDown className="w-6 h-6 text-gray-400" />
                </div>

                {/* New Plan */}
                <div className={`bg-gradient-to-r ${selectedPlan.color} bg-opacity-10 rounded-lg p-4 border-2 border-opacity-20`}>
                  <p className="text-sm text-gray-600 mb-1">New Plan</p>
                  <div className="flex items-center space-x-2 mb-2">
                    {React.createElement(selectedPlan.icon, { className: "w-5 h-5 text-gray-700" })}
                    <span className="font-semibold text-gray-900">{selectedPlan.name}</span>
                    <span className="font-bold text-lg">
                      ${selectedPlan.price}{selectedPlan.period !== 'one-time' && selectedPlan.price > 0 && `/${selectedPlan.period}`}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{selectedPlan.description}</p>
                </div>

                {/* Features Preview */}
                <div className="max-h-32 overflow-y-auto">
                  <p className="text-sm font-medium text-gray-900 mb-2">What you'll get:</p>
                  <ul className="space-y-1">
                    {selectedPlan.features.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                        <span className="text-xs text-gray-600">{feature}</span>
                      </li>
                    ))}
                    {selectedPlan.features.length > 4 && (
                      <li className="text-xs text-gray-500">...and {selectedPlan.features.length - 4} more features</li>
                    )}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowSubscriptionModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={`flex-1 bg-gradient-to-r ${selectedPlan.color} hover:opacity-90 text-white`}
                    onClick={() => handleSubscriptionUpgrade(selectedPlan.id)}
                    disabled={loading.subscription}
                  >
                    {loading.subscription ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        {getCurrentPlanIndex() < subscriptionPlans.findIndex(p => p.id === selectedPlan.id) ? 'Upgrade Now' : 'Change Plan'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  disabled={changingPassword}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter your current password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    disabled={changingPassword}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter your new password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    disabled={changingPassword}
                  />
                  <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Confirm your new password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    disabled={changingPassword}
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 border-t flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                disabled={changingPassword}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={
                  changingPassword || 
                  !passwordData.currentPassword || 
                  !passwordData.newPassword || 
                  !passwordData.confirmPassword ||
                  passwordData.newPassword !== passwordData.confirmPassword ||
                  passwordData.newPassword.length < 8
                }
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {changingPassword ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 w-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        onSuccess={handlePaymentMethodAdded}
      />

      {/* Billing History Modal */}
      {showBillingHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Billing History</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBillingHistoryModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-150px)]">
              {billingHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No billing history yet</p>
                  <p className="text-sm mt-2">Your transaction history will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {billingHistory.map((transaction, index) => (
                    <div 
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {transaction.description || 'Subscription Payment'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {new Date(transaction.date || transaction.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">
                            ${(transaction.amount || 0).toFixed(2)}
                          </p>
                          <Badge 
                            variant={transaction.status === 'succeeded' ? 'success' : 'destructive'}
                            className="mt-1"
                          >
                            {transaction.status || 'completed'}
                          </Badge>
                        </div>
                      </div>
                      
                      {transaction.payment_method && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600 mt-2">
                          <CreditCard className="w-4 h-4" />
                          <span>
                            {transaction.payment_method.brand?.toUpperCase()} 
                            {' '}****{' '}
                            {transaction.payment_method.last4}
                          </span>
                        </div>
                      )}
                      
                      {transaction.invoice_url && (
                        <div className="mt-3">
                          <a
                            href={transaction.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download Invoice
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <Button
                onClick={() => setShowBillingHistoryModal(false)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Cancel Subscription</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellationData({ reason: '', feedback: '' });
                  }}
                  disabled={cancellingSubscription}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Warning Message */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Are you sure?</strong> Cancelling your subscription will:
                  </p>
                  <ul className="text-sm text-yellow-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Keep your access until the end of your billing period</li>
                    <li>Downgrade you to the free plan after that</li>
                    <li>Remove access to premium features</li>
                  </ul>
                </div>

                {/* Current Plan Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Current Plan</p>
                  <p className="font-semibold text-gray-900">
                    {subscriptionPlans.find(p => p.id === user?.subscription_plan)?.name || user?.subscription_plan}
                  </p>
                  {subscriptionStatus?.current_period_end && (
                    <p className="text-xs text-gray-500 mt-1">
                      Access until: {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {/* Cancellation Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Why are you cancelling? (Optional)
                  </label>
                  <select
                    value={cancellationData.reason}
                    onChange={(e) => setCancellationData(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={cancellingSubscription}
                  >
                    <option value="">Select a reason...</option>
                    <option value="too_expensive">Too expensive</option>
                    <option value="not_using">Not using enough</option>
                    <option value="missing_features">Missing features I need</option>
                    <option value="found_alternative">Found an alternative</option>
                    <option value="temporary">Only needed temporarily</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Additional Feedback */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional feedback (Optional)
                  </label>
                  <textarea
                    value={cancellationData.feedback}
                    onChange={(e) => setCancellationData(prev => ({ ...prev, feedback: e.target.value }))}
                    placeholder="Tell us how we can improve..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={3}
                    disabled={cancellingSubscription}
                  />
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50 border-t flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellationData({ reason: '', feedback: '' });
                }}
                disabled={cancellingSubscription}
              >
                Keep Subscription
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-cancel-subscription-btn"
              >
                {cancellingSubscription ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Yes, Cancel Subscription
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default AccountPage;